<template>
    <HeaderCardsLayout v-if="user" variant="No-Logo">
        <template #headline>{{ $t('deactivate-user') }}</template>
        <template #icon>
            <BlockIcon class="w-12 h-12" />
        </template>
        <template #buttons>
            <LinkButton variant="Neutral-Light" size="Base" :to="{ name: 'user-details', params: { userId: user.id } }">
                <template #icon>
                    <CloseIcon class="size-5" />
                </template>
                {{ $t('cancel') }}
            </LinkButton>
            <Button variant="Danger-Strong" size="Base" @click="onDeactivateUser">
                <template #icon>
                    <BlockIcon class="size-5" />
                </template>
                {{ $t('deactivate-submit') }}
            </Button>
        </template>
        <template #breadcrumb>
            <Breadcrumb v-if="user" :headline="$t('breadcrumb')" :current-page="$t('deactivate-user')">
                <BreadcrumbLink :target="{ name: 'users' }">
                    <UsersIcon class="mr-1 shrink-0" />
                    {{ $t('users') }}
                </BreadcrumbLink>
                <BreadcrumbLink :target="{ name: 'user-details', params: { userId } }">
                    {{ user.firstName }} {{ user.lastName }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #body>
            <div v-if="user">
                <PaddedContent class="bg-gray-100">
                    <PersonInfo
                        :name="`${user.lastName}, ${user.firstName}`"
                        :job-title="user.jobTitle"
                        :company="user.company"
                        variant="Inactive"
                    >
                        <template #avatar>
                            <InitialsWithBackground
                                v-if="!user.avatarUrl"
                                class="size-12 leading-12 shrink-0"
                                :first-name="user.firstName"
                                :last-name="user.lastName"
                                variant="Gray"
                            />
                            <img
                                v-else
                                :src="user.avatarUrl"
                                alt=""
                                class="rounded-full size-12 object-cover shrink-0"
                            />
                        </template>
                    </PersonInfo>
                </PaddedContent>

                <PaddedContent class="space-y-4">
                    <AccentCard v-if="user.passwordLogin" variant="Gray">
                        <template #header="{ headlineId }">
                            <LoginMethodHeader
                                :headline-id="headlineId"
                                variant="Developer-Account"
                                :is-gray-scale="true"
                            >
                                <template #headline>
                                    {{ $t('email-password-login') }}
                                </template>
                            </LoginMethodHeader>
                        </template>
                        <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                            <CredentialsInfo :email="user.passwordLogin.email" :show-password-hint="true" />
                        </div>
                        <template #footer>
                            <div class="flex flex-row items-center gap-1.5 p-1.5 pl-2">
                                <div class="size-5 flex items-center justify-center">
                                    <BlockIcon class="size-4 shrink-0" />
                                </div>
                                <i18n path="user-password-deactivate-message" tag="span" class="text-sm font-light">
                                    <template #login="{ method }">
                                        <span class="font-semibold">{{ method }}</span>
                                    </template>
                                    <template #action="{ actionLabel }">
                                        <span class="font-semibold">{{ actionLabel }}</span>
                                    </template>
                                    <template #secondAction="{ secondActionLabel }">
                                        <span class="font-semibold">{{ secondActionLabel }}</span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </AccentCard>
                    <AccentCard v-for="sso of user.ssoLogins" :key="sso.id" variant="Gray">
                        <template #header="{ headlineId }">
                            <LoginMethodHeader
                                :headline-id="headlineId"
                                :variant="sso.provider === SsoProvider.SsoKm ? 'Koelnmesse-SSO' : 'Generic-SSO'"
                                :is-gray-scale="true"
                            >
                                <template #headline>
                                    {{ $t('provider-login-header', { provider: sso.provider }) }}
                                </template>
                            </LoginMethodHeader>
                        </template>
                        <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                            <CredentialsInfo :email="sso.email ?? ''" :show-password-hint="false" />
                        </div>
                        <template #footer>
                            <div class="flex flex-row items-center gap-1.5 p-1.5 pl-2">
                                <div class="size-5 flex items-center justify-center">
                                    <BlockIcon class="size-4 shrink-0" />
                                </div>
                                <i18n path="user-sso-deactivate-message" tag="span" class="text-sm font-light">
                                    <template #login="{ method }">
                                        <span class="font-semibold">{{ method }}</span>
                                    </template>
                                    <template #action="{ actionLabel }">
                                        <span class="font-semibold">{{ actionLabel }}</span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </AccentCard>
                </PaddedContent>
                <PaddedContent class="bg-white pt-3">
                    <section>
                        <h2 class="font-semibold text-gray-700 text-xl mb-2">
                            {{ $t('deactivate-user-result-header') }}
                        </h2>
                        <CheckList>
                            <CheckListItem>
                                <i18n path="no-content-for-account">
                                    <template #action="{ actionLabel }">
                                        <strong class="font-semibold">{{ actionLabel }}</strong>
                                    </template>
                                </i18n>
                            </CheckListItem>
                            <CheckListItem>
                                <i18n path="no-lists-for-account">
                                    <template #location="{ locationLabel }">
                                        <strong class="font-semibold">{{ locationLabel }}</strong>
                                    </template>
                                </i18n>
                            </CheckListItem>
                            <CheckListItem>
                                <i18n path="no-avatar-for-account">
                                    <template #object="{ objectLabel }">
                                        <strong class="font-semibold">{{ objectLabel }}</strong>
                                    </template>
                                </i18n>
                            </CheckListItem>
                            <CheckListItem>
                                <i18n path="internal-links-remain">
                                    <template #links="{ linkLabel }">
                                        <strong class="font-semibold">{{ linkLabel }}</strong>
                                    </template>
                                </i18n>
                            </CheckListItem>
                            <CheckListItem>
                                <i18n path="account-can-be-reactivated">
                                    <template #action="{ actionLabel }">
                                        <strong class="font-semibold">{{ actionLabel }}</strong>
                                    </template>
                                    <template #userPage="{ userPageLinkText }">
                                        <TextLink :to="{ name: 'users' }">
                                            {{ userPageLinkText }}
                                        </TextLink>
                                    </template>
                                </i18n>
                            </CheckListItem>
                        </CheckList>
                    </section>
                </PaddedContent>
                <PaddedContent class="bg-white mt-4 pt-3">
                    <h2 class="font-semibold text-gray-700 text-xl mb-2">
                        {{ $t('permissions') }}
                    </h2>
                    <RadioButtonGroup
                        :label="$t('access-permission')"
                        name="access-permission"
                        :is-required="true"
                        :options="accessPermissionOptions"
                    />
                </PaddedContent>
                <div class="bg-red-700 text-red-50">
                    <div class="flex flex-row items-center gap-1.5 p-1.5 pl-2">
                        <div class="size-5 flex items-center justify-center">
                            <BlockIcon class="size-4 shrink-0" />
                        </div>
                        <i18n path="user-sso-deactivate-access-permission" tag="span" class="text-sm font-light">
                            <template #result="{ resultLabel }">
                                <span class="font-semibold">{{ resultLabel }}</span>
                            </template>
                        </i18n>
                    </div>
                </div>
            </div>
        </template>
    </HeaderCardsLayout>
</template>

<script setup lang="ts">
import AccentCard from '@/components/AccentCard/AccentCard.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import CheckList from '@/components/CheckList/CheckList.vue';
import CheckListItem from '@/components/CheckList/CheckListItem.vue';
import CredentialsInfo from '@/components/CredentialsInfo/CredentialsInfo.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import BlockIcon from '@/components/Icon/BlockIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import LoginMethodHeader from '@/components/LoginMethodHeader/LoginMethodHeader.vue';
import PersonInfo from '@/components/PersonInfo/PersonInfo.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import { SsoProvider } from '@/generated/graphql.ts';
import { $t } from '@/plugins/fluent.ts';
import router from '@/plugins/router';
import { computed } from 'vue';
import InitialsWithBackground from '../components/InitialsWithBackground.vue';
import { useUser } from '../composables/useUser';

const props = defineProps<{ userId: string }>();

const userId = computed(() => props.userId);

const { user, deactivateUser } = useUser(userId);

async function onDeactivateUser() {
    await deactivateUser();
    router.push({ name: 'user-details', params: { userId: userId.value } });
}

const accessPermissionOptions = [
    {
        id: 'read-and-write',
        label: $t('read-and-write'),
        isChecked: false,
        isDisabled: true,
        value: 'read-and-write',
    },
    {
        id: 'read',
        label: $t('read'),
        isChecked: false,
        isDisabled: true,
        value: 'read',
    },
    {
        id: 'no-read-or-write',
        label: $t('no-read-or-write'),
        isChecked: true,
        isDisabled: false,
        value: 'no-read-or-write',
        helpButtonText: $t('no-read-or-write-help'),
    },
];
</script>
